import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import relativeTime from 'dayjs/plugin/relativeTime'
import duration from 'dayjs/plugin/duration'

dayjs.extend(customParseFormat)
dayjs.extend(advancedFormat)
dayjs.extend(isBetween)
dayjs.extend(isSameOrAfter)
dayjs.extend(relativeTime)
dayjs.extend(duration)

// moment's textual tokens (MMM/MMMM/ddd/dddd) parse case-insensitively -
// moment('04 SEP 2026', 'D MMM YYYY') is valid. dayjs's customParseFormat
// plugin requires an exact case match against its locale data ('Sep', not
// 'SEP' or 'sep') and returns Invalid Date otherwise. Title-casing every
// alphabetic run before parsing normalises any input casing to what dayjs
// expects, with no effect on purely numeric formats.
const normalizeWordCase = (str) => str.replace(
  /[a-zA-Z]+/g,
  (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
)

// dayjs's customParseFormat plugin can't reliably parse a leading weekday
// token ('ddd'/'dddd') immediately adjacent to a numeric day token - it
// returns Invalid Date even when the equivalent moment(str, fmt) call
// parses correctly (e.g. moment('Fri 4 Sep 2026', 'ddd D MMM YYYY')).
// This strips the weekday token before parsing, then re-validates it
// against the parsed date's actual weekday - matching moment's behaviour
// of invalidating a string whose weekday name doesn't match its date.
const WEEKDAY_FMT_RE = /^(dddd|ddd)([,\s]*)/
const WEEKDAY_STR_RE = /^(mon|tue|wed|thu|fri|sat|sun)[a-z]*[,\s]*/i

// Case-insensitive, weekday-aware replacement for dayjs(str, fmt). Use this
// for every 2-argument parse in the app instead of calling dayjs(str, fmt)
// directly, so casing and weekday-prefixed formats both behave like moment.
export const parseDate = (str, fmt) => {
  const normalized = normalizeWordCase(str)
  const fmtMatch = fmt.match(WEEKDAY_FMT_RE)
  if (!fmtMatch) return dayjs(normalized, fmt)

  const strMatch = normalized.match(WEEKDAY_STR_RE)
  if (!strMatch) return dayjs(NaN)

  const strippedFmt = fmt.slice(fmtMatch[0].length)
  const strippedStr = normalized.slice(strMatch[0].length)
  const parsed = dayjs(strippedStr, strippedFmt)
  if (!parsed.isValid()) return parsed

  const expectedWeekday = strMatch[1].toLowerCase().slice(0, 3)
  const actualWeekday = parsed.format('ddd').toLowerCase()
  if (actualWeekday !== expectedWeekday) return dayjs(NaN)
  return parsed
}

// Back-compat alias - existing call sites import parseWithWeekday.
export const parseWithWeekday = parseDate

export default dayjs
